import PRODUCTS from "@/data/xortec_products.json";

export { PRODUCTS };

export function buildCart({ edition, deviceCount, carePackage, careDuration }) {
  const ed = PRODUCTS.editions[edition];
  if (!ed) return [];
  const items = [];
  items.push({ ...ed.baseLicense, qty: 1, category: "Basis-Lizenz" });
  if (deviceCount && deviceCount > 0) {
    items.push({ ...ed.deviceLicense, qty: deviceCount, category: "Device-Lizenz" });
  }
  if (carePackage && carePackage !== "none" && careDuration && deviceCount > 0) {
    const careKey = carePackage === "carePremium" ? "carePremium" : "carePlus";
    const care = ed[careKey] && ed[careKey][careDuration];
    if (care) {
      const packageLabel = PRODUCTS.carePackages[careKey].label;
      items.push({
        ...care,
        name: `${packageLabel} – ${care.durationLabel}`,
        description: `${packageLabel} für eine ${edition === "corporate" ? "Corporate" : edition === "expert" ? "Expert" : edition === "professional" ? "Professional+" : "Express+"} Device License`,
        qty: deviceCount,
        category: packageLabel,
      });
    }
  }
  return items;
}

// ---------------------------------------------------------------------------
// Konfigurationsmodus: freie Auswahl von Lizenzen & Care-Paketen mit Mengen
// ---------------------------------------------------------------------------
export const EDITION_ORDER = ["express", "professional", "expert", "corporate"];
export const EDITION_LABELS = {
  express: "XProtect Express+",
  professional: "XProtect Professional+",
  expert: "XProtect Expert",
  corporate: "XProtect Corporate",
};
export const EDITION_SHORT = { express: "Express+", professional: "Professional+", expert: "Expert", corporate: "Corporate" };
export const EXPRESS_MAX_DEVICES = 100;

/** Alle bestellbaren Positionen einer Edition (Basis, Device, Care Plus/Premium je Laufzeit). */
export function buildCatalog(edition) {
  const ed = PRODUCTS.editions[edition];
  if (!ed) return [];
  const items = [];
  items.push({ key: `${edition}:base`, edition, group: "license", category: "Basis-Lizenz", ...ed.baseLicense });
  items.push({ key: `${edition}:device`, edition, group: "license", category: "Device-Lizenz", ...ed.deviceLicense });
  for (const careKey of ["carePlus", "carePremium"]) {
    const label = PRODUCTS.carePackages[careKey].label;
    for (const d of PRODUCTS.durationOptions) {
      const c = ed[careKey] && ed[careKey][d.key];
      if (!c) continue;
      items.push({
        key: `${edition}:${careKey}:${d.key}`,
        edition,
        group: careKey,
        careKey,
        category: label,
        duration: d.key,
        durationLabel: d.label,
        years: d.years,
        name: `${label} – ${d.label}`,
        description: `${label} für eine ${EDITION_SHORT[edition]} Device License (${d.years})`,
        manufacturerNo: c.manufacturerNo,
        xortecNo: c.xortecNo,
        url: `https://xortec.de/search?search=${encodeURIComponent(c.manufacturerNo)}`,
      });
    }
  }
  return items;
}

let FULL_CATALOG = null;
export function fullCatalog() {
  if (!FULL_CATALOG) FULL_CATALOG = EDITION_ORDER.flatMap(buildCatalog);
  return FULL_CATALOG;
}

/** Warenkorb aus Mengen-Map { [key]: qty } – nur Positionen mit Menge > 0, in Katalogreihenfolge. */
export function cartFromQuantities(quantities) {
  const q = quantities || {};
  return fullCatalog()
    .filter((i) => (Number(q[i.key]) || 0) > 0)
    .map((i) => ({ ...i, qty: Number(q[i.key]) || 0 }));
}

/** Mengen-Map aus einer Assistenten-Empfehlung + LicenseConfigurator-Zustand ableiten. */
export function quantitiesFromRecommendation({ edition, deviceCount, carePackage, careDuration }) {
  if (!edition || !PRODUCTS.editions[edition]) return {};
  const devices = Math.max(0, Number(deviceCount) || 0);
  const q = { [`${edition}:base`]: 1 };
  if (devices > 0) q[`${edition}:device`] = devices;
  if (devices > 0 && carePackage && carePackage !== "none") {
    const careKey = carePackage === "carePremium" ? "carePremium" : "carePlus";
    const dur = careDuration && PRODUCTS.editions[edition][careKey]?.[careDuration] ? careDuration : "1";
    q[`${edition}:${careKey}:${dur}`] = devices;
  }
  return q;
}

/** Zusammenfassung je Edition + fachliche Hinweise (Milestone-Lizenzregeln). */
export function summarizeConfiguration(quantities) {
  const q = quantities || {};
  const get = (k) => Number(q[k]) || 0;
  const perEdition = {};
  const warnings = [];
  let totalDevices = 0;
  let totalCare = 0;

  for (const ed of EDITION_ORDER) {
    const base = get(`${ed}:base`);
    const devices = get(`${ed}:device`);
    let carePlus = 0;
    let carePremium = 0;
    for (const d of PRODUCTS.durationOptions) {
      carePlus += get(`${ed}:carePlus:${d.key}`);
      carePremium += get(`${ed}:carePremium:${d.key}`);
    }
    const care = carePlus + carePremium;
    if (!(base || devices || care)) continue;
    perEdition[ed] = { base, devices, carePlus, carePremium, care };
    totalDevices += devices;
    totalCare += care;
    const label = EDITION_SHORT[ed];

    if ((devices > 0 || care > 0) && base === 0) {
      warnings.push({ level: "error", edition: ed, text: `${label}: Es fehlt die Basis-Lizenz – jedes XProtect-System benötigt genau eine Basis-Lizenz.` });
    }
    if (base > 1) {
      warnings.push({ level: "info", edition: ed, text: `${label}: ${base} Basis-Lizenzen entsprechen ${base} getrennten Systemen (Management Server).` });
    }
    if (ed === "express" && devices > EXPRESS_MAX_DEVICES * Math.max(base, 1)) {
      warnings.push({ level: "error", edition: ed, text: `Express+ unterstützt maximal ${EXPRESS_MAX_DEVICES} Geräte je System. Für ${devices} Geräte empfehlen wir Professional+ oder höher.` });
    }
    if (base > 0 && devices === 0) {
      warnings.push({ level: "warning", edition: ed, text: `${label}: Basis-Lizenz ohne Device-Lizenzen – bitte Anzahl der Kameras/Kanäle ergänzen.` });
    }
    if (care > 0 && devices > 0 && care !== devices) {
      warnings.push({ level: "warning", edition: ed, text: `${label}: Care deckt ${care} von ${devices} Device-Lizenzen ab. Milestone Care muss für alle Geräte eines Systems abgeschlossen werden.` });
    }
    if (care > 0 && devices === 0) {
      warnings.push({ level: "warning", edition: ed, text: `${label}: Care-Lizenzen ohne zugehörige Device-Lizenzen.` });
    }
    if (carePlus > 0 && carePremium > 0) {
      warnings.push({ level: "info", edition: ed, text: `${label}: Care Plus und Care Premium gemischt – je System ist nur eine Care-Stufe möglich (Premium enthält Plus).` });
    }
    if (devices > 0 && care === 0) {
      warnings.push({ level: "info", edition: ed, text: `${label}: Ohne Care-Paket keine Software-Upgrades nach Ablauf der enthaltenen Care-Laufzeit.` });
    }
  }

  const cart = cartFromQuantities(q);
  return {
    perEdition,
    warnings,
    totals: {
      editions: Object.keys(perEdition),
      devices: totalDevices,
      care: totalCare,
      positions: cart.length,
      units: cart.reduce((s, i) => s + i.qty, 0),
    },
  };
}

// --- Configuration code encoding/decoding (compact, URL-safe) ---
// Uses TextEncoder + Base64URL. Not encrypted – designed as a portable share code.
export function encodeConfigCode(payload) {
  try {
    const json = JSON.stringify(payload);
    const bytes = new TextEncoder().encode(json);
    let bin = "";
    for (let i = 0; i < bytes.byteLength; i++) bin += String.fromCharCode(bytes[i]);
    const b64 = btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
    // Prefix + dash-formatted for readability
    const raw = `XT1${b64}`;
    return raw.match(/.{1,6}/g).join("-");
  } catch (e) {
    return null;
  }
}

export function decodeConfigCode(code) {
  try {
    const clean = String(code || "").replace(/[^A-Za-z0-9_-]/g, "");
    if (!clean.startsWith("XT1")) return null;
    let b64 = clean.slice(3).replace(/-/g, "+").replace(/_/g, "/");
    while (b64.length % 4) b64 += "=";
    const bin = atob(b64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    const json = new TextDecoder().decode(bytes);
    return JSON.parse(json);
  } catch (e) {
    return null;
  }
}
